import { noop } from 'lodash';
import { useRouter } from 'next/router';
import { createContext, memo, type ProviderProps, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { reportErrorToBugsnag } from '@air/utils-error';

import { isDevOrTestStage } from '~/swr-hooks/utils';

export type KanbanOffsetContextType = {
  kanbanOffset: number;
  setKanbanOffset: (offset: number) => void;
};

export const initialKanbanOffsetContext: KanbanOffsetContextType = {
  kanbanOffset: 0,
  setKanbanOffset: noop,
};

export const KanbanOffsetContext = createContext<KanbanOffsetContextType>(initialKanbanOffsetContext);

export type KanbanOffsetContextProviderProps = Pick<ProviderProps<KanbanOffsetContextType>, 'children'>;

export const KanbanOffsetContextProvider = memo(({ children }: KanbanOffsetContextProviderProps) => {
  const router = useRouter();
  const [kanbanOffset, setKanbanOffset] = useState(0);

  const value = useMemo(() => ({ kanbanOffset, setKanbanOffset }), [kanbanOffset]);

  const handleResetKanbanOffset = useCallback((_url: string, { shallow }: { shallow?: boolean } = {}) => {
    // When a route change happens, we want to reset the offset to 0.
    // We do this because we add the height of elements before the kanban into this context provider
    // and those values can change between pages.
    if (!shallow) {
      setKanbanOffset(0);
    }
  }, []);

  useEffect(() => {
    router.events.on('routeChangeComplete', handleResetKanbanOffset);

    return () => {
      router.events.off('routeChangeComplete', handleResetKanbanOffset);
    };
  }, [handleResetKanbanOffset, router.events]);

  return <KanbanOffsetContext.Provider value={value}>{children}</KanbanOffsetContext.Provider>;
});

KanbanOffsetContextProvider.displayName = 'KanbanOffsetContextProvider';

export const useKanbanOffsetContext = () => {
  const context = useContext(KanbanOffsetContext);

  if (context === initialKanbanOffsetContext) {
    const error = 'useKanbanOffsetContext must be used within a KanbanOffsetContextProvider';

    if (isDevOrTestStage()) {
      throw error;
    } else {
      reportErrorToBugsnag({
        error,
        context: error,
      });
    }
  }

  return context;
};
